import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';

import {
	PENDING_STOCK_SOURCE_TYPE,
	ROLE_PERMISSIONS,
	STATUS,
} from '../../../common/constants';
import { GET_PENDING_STOCK_LIST } from '../../../common/graphql/pendingStock';
import { useUserContext } from '../../../common/utils/context';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button, Picker } from '../../components';
import { ActionDock, EmptyState, Screen } from '../../design/components';
import Text from '../../components/text/Text';
import { DateSelectorInput } from '../../components/input';
import { UserSelector, WarehouseSelector } from '../../components/selector';

import PendingStockList from './PendingStockList';
import PendingStockCreateReturnDialog from './PendingStockCreateReturnDialog';

const SOURCE_FILTERS = {
	DRIVER_RETURNS: 'driverReturns',
	ALL_PENDING: 'allPending',
};

const SOURCE_FILTER_OPTIONS = [
	{ label: 'Driver returns', value: SOURCE_FILTERS.DRIVER_RETURNS },
	{ label: 'All pending', value: SOURCE_FILTERS.ALL_PENDING },
];

const SHOULD_COLLAPSE_FILTERS_ON_SCROLL = Platform.OS !== 'web';
const FILTER_HIDE_SCROLL_OFFSET = 24;
const FILTER_SHOW_SCROLL_OFFSET = 6;
const FILTER_SCROLL_EVENT_THROTTLE = 16;

const PendingStockListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission, isPicker } = useUserContext();
	const [selectedDriver, setSelectedDriver] = useState(null);
	const [selectedWarehouse, setSelectedWarehouse] = useState(null);
	const [runDate, setRunDate] = useState(() => moment().format('YYYY-MM-DD'));
	const [sourceFilter, setSourceFilter] = useState(SOURCE_FILTERS.DRIVER_RETURNS);
	const [isCreateReturnDialogOpen, setIsCreateReturnDialogOpen] = useState(false);
	const [listRefreshVersion, setListRefreshVersion] = useState(0);
	const [areFiltersCollapsed, setAreFiltersCollapsed] = useState(false);
	const areFiltersCollapsedRef = useRef(false);
	const previousScrollOffsetRef = useRef(0);

	const canViewPendingStock = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_STOCK_PAGE) || isPicker);
	}, [hasPermission, isPicker]);

	const canModifyStock = useMemo(() => {
		// Pickers process AVC driver returns here without gaining access to the wider stock tools.
		return Boolean(hasPermission(ROLE_PERMISSIONS.MODIFY_STOCK_LEVELS) || isPicker);
	}, [hasPermission, isPicker]);

	const canViewStockDeliveryDetail = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_DETAIL_PAGE));
	}, [hasPermission]);

	const handleOpenStockDelivery = useMemo(() => {
		return (stockDeliveryId) => {
			if (!stockDeliveryId || !canViewStockDeliveryDetail) {
				return;
			}
			navigation.navigate('StockDeliveryDetailView', { id: stockDeliveryId });
		};
	}, [canViewStockDeliveryDetail, navigation]);

	const initialFilter = useMemo(() => {
		return {
			status: STATUS.PENDING,
			...(sourceFilter === SOURCE_FILTERS.DRIVER_RETURNS ? { sourceType: PENDING_STOCK_SOURCE_TYPE.REMOVED } : {}),
			...(selectedDriver?.id ? { driver: selectedDriver.id } : {}),
			...(selectedWarehouse?.id ? { warehouse: selectedWarehouse.id } : {}),
			...(runDate ? { runDate } : {}),
		};
	}, [runDate, selectedDriver?.id, selectedWarehouse?.id, sourceFilter]);

	const listKey = useMemo(() => (
		JSON.stringify({ initialFilter, listRefreshVersion })
	), [initialFilter, listRefreshVersion]);

	const updateFiltersCollapsed = useCallback((nextValue) => {
		if (areFiltersCollapsedRef.current === nextValue) {
			return;
		}

		areFiltersCollapsedRef.current = nextValue;
		setAreFiltersCollapsed(nextValue);
	}, []);

	const handlePendingStockScroll = useCallback((event) => {
		if (!SHOULD_COLLAPSE_FILTERS_ON_SCROLL) {
			return;
		}

		const nextScrollOffset = Math.max(0, Number(event?.nativeEvent?.contentOffset?.y) || 0);
		const isScrollingDown = nextScrollOffset > previousScrollOffsetRef.current;
		previousScrollOffsetRef.current = nextScrollOffset;

		if (nextScrollOffset <= FILTER_SHOW_SCROLL_OFFSET) {
			updateFiltersCollapsed(false);
			return;
		}

		if (isScrollingDown && nextScrollOffset > FILTER_HIDE_SCROLL_OFFSET) {
			updateFiltersCollapsed(true);
		}
	}, [updateFiltersCollapsed]);

	const clearDriver = useCallback(() => {
		setSelectedDriver(null);
	}, []);

	const clearWarehouse = useCallback(() => {
		setSelectedWarehouse(null);
	}, []);

	const openCreateReturnDialog = useCallback(() => {
		if (!canModifyStock || !runDate) {
			return;
		}

		setIsCreateReturnDialogOpen(true);
	}, [canModifyStock, runDate]);

	const closeCreateReturnDialog = useCallback(() => {
		setIsCreateReturnDialogOpen(false);
	}, []);

	const refreshPendingStockList = useCallback(() => {
		setListRefreshVersion(previous => previous + 1);
	}, []);

	useEffect(() => {
		previousScrollOffsetRef.current = 0;
		updateFiltersCollapsed(false);
	}, [listKey, updateFiltersCollapsed]);

	if (!canViewPendingStock) {
		return (
			<Screen heading="Pending stock">
				<EmptyState icon="lock" title="You do not have access to pending stock." />
			</Screen>
		);
	}

	return (
		<Screen
			heading="Pending stock"
			subheading="Process driver returns by run date"
			scroll={false}
			dock={(
				<ActionDock
					action={{
						label: 'Add returned stock',
						icon: 'plus',
						onPress: openCreateReturnDialog,
						disabled: !canModifyStock || !runDate,
					}}
				/>
			)}
		>
			{!areFiltersCollapsed && (
				<View style={styles.filters}>
					<View style={styles.filterBlock}>
						<Text style={styles.filterLabel}>Run date</Text>
						<DateSelectorInput
							value={runDate}
							onChange={setRunDate}
							clearLabel="Any"
						/>
					</View>
					<View style={styles.filterBlock}>
						<Text style={styles.filterLabel}>Driver</Text>
						<View style={styles.filterRow}>
							<View style={styles.filterSelector}>
								<UserSelector
									value={selectedDriver}
									onChange={setSelectedDriver}
									placeholder="All drivers"
								/>
							</View>
							{selectedDriver && (
								<Button
									label="All"
									onPress={clearDriver}
								/>
							)}
						</View>
					</View>
					<View style={styles.filterBlock}>
						<Text style={styles.filterLabel}>Warehouse</Text>
						<View style={styles.filterRow}>
							<View style={styles.filterSelector}>
								<WarehouseSelector
									value={selectedWarehouse}
									onChange={setSelectedWarehouse}
									placeholder="All warehouses"
								/>
							</View>
							{selectedWarehouse && (
								<Button
									label="All"
									onPress={clearWarehouse}
								/>
							)}
						</View>
					</View>
					<View style={styles.filterBlock}>
						<Text style={styles.filterLabel}>Queue</Text>
						<Picker
							selectedValue={sourceFilter}
							onValueChange={setSourceFilter}
							options={SOURCE_FILTER_OPTIONS}
							width="100%"
						/>
					</View>
				</View>
			)}
			<PendingStockList
				key={listKey}
				query={GET_PENDING_STOCK_LIST}
				// Reverse pick order: products picked last sit on top of the
				// returned tub, so they are processed first.
				initialSortBy="pickOrder"
				initialSortDirection={-1}
				initialPerPage={20}
				initialFilter={initialFilter}
				listId="mobile_pending_stock_list"
				onScroll={SHOULD_COLLAPSE_FILTERS_ON_SCROLL ? handlePendingStockScroll : undefined}
				scrollEventThrottle={SHOULD_COLLAPSE_FILTERS_ON_SCROLL ? FILTER_SCROLL_EVENT_THROTTLE : undefined}
				customProps={{
					canModifyStock,
					canViewStockDeliveryDetail,
					onOpenStockDelivery: handleOpenStockDelivery,
				}}
			/>
			<PendingStockCreateReturnDialog
				isOpen={isCreateReturnDialogOpen}
				runDate={runDate}
				onClose={closeCreateReturnDialog}
				onCreated={refreshPendingStockList}
			/>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	filters: {
		marginHorizontal: space.gutter,
		marginBottom: space.sm,
		backgroundColor: colors.card,
		borderRadius: radius.card,
		padding: space.md,
		gap: space.md,
	},
	filterBlock: {
		gap: space.xs,
	},
	filterLabel: {
		...type.fieldLabel,
		color: colors.text,
	},
	filterRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	filterSelector: {
		flex: 1,
	},
});

export default PendingStockListView;
